/** OceanSpace product support for staff (company mailbox, not tenant branding). */
export const STAFF_SUPPORT_EMAIL = 'support@oceanspace.fi';

export const STAFF_SUPPORT_MAILTO = `mailto:${STAFF_SUPPORT_EMAIL}`;

/**
 * Static Vite `public/help` pages. Not SPA routes — open with `<a href>`.
 * Swedish UI is not offered in the language menu; fall back to Finnish.
 */
export function staffHelpLanguage(language: string): 'fi' | 'en' {
  const base = language.toLowerCase().split(/[-_]/)[0];
  return base === 'en' ? 'en' : 'fi';
}

export function staffHelpHref(language: string): string {
  return `/help/${staffHelpLanguage(language)}.html`;
}

export type TenantSupportLinks = {
  helpUrl?: string;
  supportEmail?: string;
};

export type StaffHelpEntryKind =
  | 'osBuddyHelp'
  | 'tenantHelp'
  | 'oceanSpaceSupport'
  | 'tenantSupport';

export interface StaffHelpEntry {
  kind: StaffHelpEntryKind;
  href: string;
  newTab: boolean;
  /** Key in the `navigation` namespace. */
  labelKey: string;
}

function nonEmpty(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

/**
 * Always includes the OS Buddy quick guide and OceanSpace mailbox.
 * Tenant branding `helpUrl` / `supportEmail` are extra entries when set;
 * product labels then name OS Buddy / OceanSpace so the two destinations
 * are not confused.
 */
export function staffHelpEntries(
  language: string,
  support?: TenantSupportLinks | null,
): StaffHelpEntry[] {
  const tenantHelpUrl = nonEmpty(support?.helpUrl);
  const tenantSupportEmail = nonEmpty(support?.supportEmail);

  const entries: StaffHelpEntry[] = [
    {
      kind: 'osBuddyHelp',
      href: staffHelpHref(language),
      newTab: true,
      labelKey: tenantHelpUrl ? 'helpOsBuddy' : 'help',
    },
  ];

  if (tenantHelpUrl) {
    entries.push({
      kind: 'tenantHelp',
      href: tenantHelpUrl,
      newTab: true,
      labelKey: 'helpWorkshop',
    });
  }

  entries.push({
    kind: 'oceanSpaceSupport',
    href: STAFF_SUPPORT_MAILTO,
    newTab: false,
    labelKey: tenantSupportEmail ? 'supportOceanSpace' : 'support',
  });

  if (tenantSupportEmail) {
    entries.push({
      kind: 'tenantSupport',
      href: `mailto:${tenantSupportEmail}`,
      newTab: false,
      labelKey: 'supportWorkshop',
    });
  }

  return entries;
}
